import React from 'react';
import { Wifi, WifiOff, Loader2 } from 'lucide-react';

/**
 * <ConnectionStatusPill /> — visual indicator for WebSocket reconnect cycle.
 *
 * Renders nothing when the socket is healthy on first connect (isConnected=true,
 * reconnectAttempts=0). Once an outage occurs, the pill stays visible until the
 * socket re-establishes for at least one render tick, giving users clear
 * feedback that real-time features are degraded.
 *
 * Props:
 *   isConnected         — current WebSocket open state
 *   reconnectAttempts   — how many backoff cycles have fired since last open
 *   onReconnect         — optional click handler for a "Retry now" button
 *   className           — extra positioning utilities (defaults to fixed bottom-right)
 *   compact             — when true, omits the label (icon-only)
 */
export const ConnectionStatusPill = ({
  isConnected,
  reconnectAttempts = 0,
  onReconnect,
  className = '',
  compact = false,
}) => {
  // First-ever connect attempt OR healthy socket — nothing to show.
  if (isConnected && reconnectAttempts === 0) return null;
  if (isConnected) {
    // Briefly visible after a successful recovery so users see the green flash.
    return (
      <div
        data-testid="ws-status-pill-online"
        className={`inline-flex items-center gap-2 rounded-full bg-brand-500/15 border border-brand-400/40 px-3 py-1.5 text-xs text-brand-300 backdrop-blur-sm ${className}`}
      >
        <Wifi className="w-3.5 h-3.5" />
        {!compact && <span>Live</span>}
      </div>
    );
  }

  const isReconnecting = reconnectAttempts > 0;
  const label = isReconnecting ? `Reconnecting… (attempt ${reconnectAttempts})` : 'Connecting…';

  return (
    <div
      data-testid="ws-status-pill-offline"
      className={`inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-500/40 px-3 py-1.5 text-xs text-amber-300 backdrop-blur-sm ${className}`}
    >
      {isReconnecting ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <WifiOff className="w-3.5 h-3.5" />
      )}
      {!compact && <span>{label}</span>}
      {onReconnect && isReconnecting && (
        <button
          type="button"
          onClick={onReconnect}
          data-testid="ws-status-retry-btn"
          className="ml-1 underline underline-offset-2 hover:text-amber-200 transition-colors"
        >
          Retry now
        </button>
      )}
    </div>
  );
};

export default ConnectionStatusPill;
